import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { Heart, ArrowLeft, CheckCircle, Scale, Languages, Menu, X } from 'lucide-react';
import WhatsAppButton from '../components/WhatsAppButton';
import { EMAILJS_CONFIG } from '../emailConfig';

const BonafideCasePage = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [declarationChecked, setDeclarationChecked] = useState(false);
  
  // Initialize EmailJS with private key for strict mode
  useEffect(() => {
    emailjs.init({
      publicKey: EMAILJS_CONFIG.PUBLIC_KEY,
      privateKey: EMAILJS_CONFIG.PRIVATE_KEY
    });
  }, []);
  
  const [formData, setFormData] = useState({
    referralType: '',
    advocateName: '',
    advocateContact: '',
    advocateBarNumber: '',
    clientName: '',
    clientFatherName: '',
    clientAge: '',
    clientGender: '',
    clientPhone: '',
    clientEmail: '',
    clientAddress: '',
    clientCity: '',
    clientState: '',
    clientPincode: '',
    caseType: '',
    caseDescription: '',
    urgency: '',
    financialStatus: '',
    previousLegalHelp: '',
    documents: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isHindi = language === 'hi';

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'hi' : 'en');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!declarationChecked) {
      toast({
        title: isHindi ? "घोषणा आवश्यक" : "Declaration Required",
        description: isHindi ? "कृपया घोषणा चेकबॉक्स पर टिक करें" : "Please check the declaration checkbox",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare email template parameters - standard EmailJS names
      const templateParams = {
        to_name: 'Advocate Imran Pathan',
        from_name: formData.clientName,
        from_email: formData.clientEmail || 'Not provided',
        phone: formData.clientPhone,
        referral_type: formData.referralType === 'advocate' ? 'Through Advocate / अधिवक्ता के माध्यम से' : 'Direct from Client / सीधे क्लाइंट से',
        advocate_name: formData.advocateName || 'N/A',
        advocate_contact: formData.advocateContact || 'N/A',
        advocate_bar_number: formData.advocateBarNumber || 'N/A',
        client_name: formData.clientName,
        father_name: formData.clientFatherName,
        age: formData.clientAge,
        gender: formData.clientGender,
        address: formData.clientAddress,
        city: formData.clientCity,
        state: formData.clientState,
        pincode: formData.clientPincode,
        case_type: formData.caseType,
        message: formData.caseDescription,
        urgency: formData.urgency,
        financial_status: formData.financialStatus,
        previous_help: formData.previousLegalHelp,
        documents: formData.documents || 'None',
        reply_to: formData.clientEmail || 'advimranaligarh@gmail.com'
      };

      // Send email via EmailJS (init already done with private key)
      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.BONAFIDE_TEMPLATE_ID,
        templateParams
      );
      
      console.log('EmailJS Response:', response);
      
      toast({
        title: isHindi ? "आवेदन सबमिट हुआ" : "Application Submitted",
        description: isHindi ? 'आवेदन प्राप्त हुआ। हम जल्द ही आपसे संपर्क करेंगे।' : 'Application received. We will contact you soon.',
      });
      
      // Reset form
      setFormData({
        referralType: '',
        advocateName: '',
        advocateContact: '',
        advocateBarNumber: '',
        clientName: '',
        clientFatherName: '',
        clientAge: '',
        clientGender: '',
        clientPhone: '',
        clientEmail: '',
        clientAddress: '',
        clientCity: '',
        clientState: '',
        clientPincode: '',
        caseType: '',
        caseDescription: '',
        urgency: '',
        financialStatus: '',
        previousLegalHelp: '',
        documents: ''
      });
      setDeclarationChecked(false);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: isHindi ? "त्रुटि" : "Error",
        description: isHindi ? 'कुछ गलत हो गया। कृपया पुनः प्रयास करें।' : 'Something went wrong. Please try again.',
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white shadow-sm z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_highcourt-law/artifacts/3rxsnv1c_1000782169.png" 
                alt="Pathan & Associates Logo" 
                className="h-12 w-auto"
              />
            </div>

            <nav className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-gray-700 hover:text-blue-900 transition-colors">
                {isHindi ? 'मुख्य पृष्ठ' : 'Home'}
              </Link>
              <Link to="/#about" className="text-gray-700 hover:text-blue-900 transition-colors">
                {isHindi ? 'परिचय' : 'About'}
              </Link>
              <Link to="/#team" className="text-gray-700 hover:text-blue-900 transition-colors">
                {isHindi ? 'हमारी टीम' : 'Our Team'}
              </Link>
              <Link to="/#practice" className="text-gray-700 hover:text-blue-900 transition-colors">
                {isHindi ? 'सेवाएं' : 'Practice Areas'}
              </Link>
              <Link to="/#achievements" className="text-gray-700 hover:text-blue-900 transition-colors">
                {isHindi ? 'उपलब्धियां' : 'Achievements'}
              </Link>
              <Link to="/#contact" className="text-gray-700 hover:text-blue-900 transition-colors">
                {isHindi ? 'संपर्क' : 'Contact'}
              </Link>
              <Button onClick={toggleLanguage} variant="outline" size="sm" className="flex items-center space-x-1">
                <Languages className="h-4 w-4" />
                <span>{language === 'en' ? 'हिं' : 'EN'}</span>
              </Button>
              <Button onClick={() => navigate('/#consultation')} className="bg-amber-600 hover:bg-amber-700">
                {isHindi ? 'परामर्श बुक करें' : 'Book Consultation'}
              </Button>
            </nav>

            <div className="flex md:hidden items-center space-x-2">
              <Button onClick={toggleLanguage} variant="outline" size="sm">
                <Languages className="h-4 w-4" />
              </Button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-700">
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 space-y-3 border-t pt-4">
              <Link to="/" className="block w-full text-left text-gray-700 hover:text-blue-900">
                {isHindi ? 'मुख्य पृष्ठ' : 'Home'}
              </Link>
              <Link to="/#about" className="block w-full text-left text-gray-700 hover:text-blue-900">
                {isHindi ? 'परिचय' : 'About'}
              </Link>
              <Link to="/#team" className="block w-full text-left text-gray-700 hover:text-blue-900">
                {isHindi ? 'हमारी टीम' : 'Our Team'}
              </Link>
              <Link to="/#practice" className="block w-full text-left text-gray-700 hover:text-blue-900">
                {isHindi ? 'सेवाएं' : 'Practice Areas'}
              </Link>
              <Link to="/#achievements" className="block w-full text-left text-gray-700 hover:text-blue-900">
                {isHindi ? 'उपलब्धियां' : 'Achievements'}
              </Link>
              <Link to="/#contact" className="block w-full text-left text-gray-700 hover:text-blue-900">
                {isHindi ? 'संपर्क' : 'Contact'}
              </Link>
              <Button onClick={() => navigate('/#consultation')} className="w-full bg-amber-600 hover:bg-amber-700">
                {isHindi ? 'परामर्श बुक करें' : 'Book Consultation'}
              </Button>
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Back Button */}
          <div className="mb-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-900"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {isHindi ? 'वापस जाएं' : 'Back to Home'}
            </Button>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full mb-4 shadow-lg">
                <Heart className="h-10 w-10 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
                {isHindi ? 'बोनाफाइड केस आवेदन' : 'Bonafide Case Application'}
              </h1>
              <p className="text-gray-600 text-lg">
                {isHindi ? 'मुफ्त कानूनी सहायता के लिए आवेदन करें' : 'Apply for Free Legal Aid'}
              </p>
            </div>

            {/* Form Card */}
            <Card className="bg-white shadow-xl border-0" data-testid="bonafide-form-card">
              <CardContent className="pt-8 pb-8">
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Referral Type */}
                  <div className="bg-amber-50 p-5 rounded-xl border border-amber-200">
                    <label className="block text-sm font-semibold mb-3 text-gray-900">
                      {isHindi ? 'केस किसके माध्यम से आ रहा है? *' : 'How is the case being referred? *'}
                    </label>
                    <Select 
                      value={formData.referralType} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, referralType: value }))} 
                      required
                    >
                      <SelectTrigger data-testid="referral-type-select">
                        <SelectValue placeholder={isHindi ? 'चुनें' : 'Select'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="advocate">{isHindi ? 'अधिवक्ता के माध्यम से' : 'Through Advocate'}</SelectItem>
                        <SelectItem value="direct">{isHindi ? 'सीधे क्लाइंट से' : 'Direct from Client'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Advocate Details (if referred by advocate) */}
                  {formData.referralType === 'advocate' && (
                    <div className="border-2 border-blue-200 rounded-xl p-5 bg-blue-50">
                      <h3 className="font-bold text-gray-900 mb-4 flex items-center text-lg">
                        <Scale className="h-5 w-5 mr-2 text-blue-600" />
                        {isHindi ? 'अधिवक्ता का विवरण' : 'Advocate Details'}
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700">
                            {isHindi ? 'अधिवक्ता का नाम *' : 'Advocate Name *'}
                          </label>
                          <Input 
                            name="advocateName" 
                            value={formData.advocateName} 
                            onChange={handleInputChange} 
                            required={formData.referralType === 'advocate'}
                            placeholder={isHindi ? 'अधिवक्ता का नाम' : 'Advocate Name'}
                            data-testid="advocate-name-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700">
                            {isHindi ? 'संपर्क नंबर *' : 'Contact Number *'}
                          </label>
                          <Input 
                            name="advocateContact" 
                            value={formData.advocateContact} 
                            onChange={handleInputChange} 
                            required={formData.referralType === 'advocate'}
                            placeholder={isHindi ? 'फोन नंबर' : 'Phone Number'}
                            data-testid="advocate-contact-input"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-2 text-gray-700">
                            {isHindi ? 'बार काउंसिल नंबर' : 'Bar Council Number'}
                          </label>
                          <Input 
                            name="advocateBarNumber" 
                            value={formData.advocateBarNumber} 
                            onChange={handleInputChange} 
                            placeholder={isHindi ? 'बार काउंसिल नामांकन संख्या' : 'Bar Council Enrollment Number'}
                            data-testid="advocate-bar-number-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Client Full Details */}
                  <div className="border-2 border-green-200 rounded-xl p-5 bg-green-50">
                    <h3 className="font-bold text-gray-900 mb-4 text-lg">
                      {isHindi ? 'क्लाइंट का पूर्ण विवरण' : 'Client Full Details'}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'क्लाइंट का नाम *' : 'Client Name *'}
                        </label>
                        <Input 
                          name="clientName" 
                          value={formData.clientName} 
                          onChange={handleInputChange} 
                          required
                          placeholder={isHindi ? 'पूरा नाम' : 'Full Name'}
                          data-testid="client-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'पिता/पति का नाम *' : "Father's/Husband Name *"}
                        </label>
                        <Input 
                          name="clientFatherName" 
                          value={formData.clientFatherName} 
                          onChange={handleInputChange} 
                          required
                          placeholder={isHindi ? 'पिता/पति का नाम' : "Father's/Husband's Name"}
                          data-testid="client-father-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'आयु *' : 'Age *'}
                        </label>
                        <Input 
                          type="number" 
                          name="clientAge" 
                          value={formData.clientAge} 
                          onChange={handleInputChange} 
                          required
                          placeholder={isHindi ? 'आयु' : 'Age'}
                          data-testid="client-age-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'लिंग *' : 'Gender *'}
                        </label>
                        <Select 
                          value={formData.clientGender} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, clientGender: value }))} 
                          required
                        >
                          <SelectTrigger data-testid="client-gender-select">
                            <SelectValue placeholder={isHindi ? 'चुनें' : 'Select'} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">{isHindi ? 'पुरुष' : 'Male'}</SelectItem>
                            <SelectItem value="female">{isHindi ? 'महिला' : 'Female'}</SelectItem>
                            <SelectItem value="other">{isHindi ? 'अन्य' : 'Other'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'फोन नंबर *' : 'Phone Number *'}
                        </label>
                        <Input 
                          type="tel" 
                          name="clientPhone" 
                          value={formData.clientPhone} 
                          onChange={handleInputChange} 
                          required
                          placeholder={isHindi ? 'फोन नंबर' : 'Phone Number'}
                          data-testid="client-phone-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'ईमेल' : 'Email'}
                        </label>
                        <Input 
                          type="email" 
                          name="clientEmail" 
                          value={formData.clientEmail} 
                          onChange={handleInputChange} 
                          placeholder={isHindi ? 'ईमेल पता' : 'Email Address'}
                          data-testid="client-email-input"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'पूरा पता *' : 'Full Address *'}
                        </label>
                        <Textarea 
                          name="clientAddress" 
                          value={formData.clientAddress} 
                          onChange={handleInputChange} 
                          required
                          rows={2}
                          placeholder={isHindi ? 'पूरा पता' : 'Complete Address'}
                          data-testid="client-address-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'शहर *' : 'City *'}
                        </label>
                        <Input 
                          name="clientCity" 
                          value={formData.clientCity} 
                          onChange={handleInputChange} 
                          required
                          placeholder={isHindi ? 'शहर का नाम' : 'City Name'}
                          data-testid="client-city-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'राज्य *' : 'State *'}
                        </label>
                        <Input 
                          name="clientState" 
                          value={formData.clientState} 
                          onChange={handleInputChange} 
                          required
                          placeholder={isHindi ? 'राज्य' : 'State'}
                          data-testid="client-state-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'पिनकोड *' : 'Pincode *'}
                        </label>
                        <Input 
                          name="clientPincode" 
                          value={formData.clientPincode} 
                          onChange={handleInputChange} 
                          required
                          placeholder={isHindi ? 'पिनकोड' : 'Pincode'}
                          data-testid="client-pincode-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Case Details */}
                  <div className="border-2 border-purple-200 rounded-xl p-5 bg-purple-50">
                    <h3 className="font-bold text-gray-900 mb-4 text-lg">
                      {isHindi ? 'मामले का विवरण' : 'Case Details'}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'मामले का प्रकार *' : 'Case Type *'}
                        </label>
                        <Select 
                          value={formData.caseType} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, caseType: value }))} 
                          required
                        >
                          <SelectTrigger data-testid="case-type-select">
                            <SelectValue placeholder={isHindi ? 'मामले का प्रकार चुनें' : 'Select Case Type'} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="criminal">{isHindi ? 'आपराधिक' : 'Criminal'}</SelectItem>
                            <SelectItem value="civil">{isHindi ? 'सिविल' : 'Civil'}</SelectItem>
                            <SelectItem value="family">{isHindi ? 'पारिवारिक' : 'Family'}</SelectItem>
                            <SelectItem value="property">{isHindi ? 'संपत्ति' : 'Property'}</SelectItem>
                            <SelectItem value="consumer">{isHindi ? 'उपभोक्ता' : 'Consumer'}</SelectItem>
                            <SelectItem value="labor">{isHindi ? 'श्रम/रोज़गार' : 'Labor/Employment'}</SelectItem>
                            <SelectItem value="other">{isHindi ? 'अन्य' : 'Other'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'मामले का संक्षिप्त विवरण *' : 'Brief Case Description *'}
                        </label>
                        <Textarea 
                          name="caseDescription" 
                          value={formData.caseDescription} 
                          onChange={handleInputChange} 
                          required
                          rows={4}
                          placeholder={isHindi ? 'अपने मामले का विस्तार से वर्णन करें' : 'Describe your case in detail'}
                          data-testid="case-description-input"
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700">
                            {isHindi ? 'तात्कालिकता *' : 'Urgency *'}
                          </label>
                          <Select 
                            value={formData.urgency} 
                            onValueChange={(value) => setFormData(prev => ({ ...prev, urgency: value }))} 
                            required
                          >
                            <SelectTrigger data-testid="urgency-select">
                              <SelectValue placeholder={isHindi ? 'चुनें' : 'Select'} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="immediate">{isHindi ? 'तत्काल' : 'Immediate'}</SelectItem>
                              <SelectItem value="urgent">{isHindi ? 'जरूरी' : 'Urgent'}</SelectItem>
                              <SelectItem value="normal">{isHindi ? 'सामान्य' : 'Normal'}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700">
                            {isHindi ? 'आर्थिक स्थिति *' : 'Financial Status *'}
                          </label>
                          <Select 
                            value={formData.financialStatus} 
                            onValueChange={(value) => setFormData(prev => ({ ...prev, financialStatus: value }))} 
                            required
                          >
                            <SelectTrigger data-testid="financial-status-select">
                              <SelectValue placeholder={isHindi ? 'चुनें' : 'Select'} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="bpl">{isHindi ? 'बीपीएल (गरीबी रेखा से नीचे)' : 'BPL (Below Poverty Line)'}</SelectItem>
                              <SelectItem value="low-income">{isHindi ? 'कम आय' : 'Low Income'}</SelectItem>
                              <SelectItem value="unemployed">{isHindi ? 'बेरोजगार' : 'Unemployed'}</SelectItem>
                              <SelectItem value="other">{isHindi ? 'अन्य' : 'Other'}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'क्या पहले कोई कानूनी सहायता ली थी? *' : 'Previous Legal Help? *'}
                        </label>
                        <Select 
                          value={formData.previousLegalHelp} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, previousLegalHelp: value }))} 
                          required
                        >
                          <SelectTrigger data-testid="previous-legal-help-select">
                            <SelectValue placeholder={isHindi ? 'चुनें' : 'Select'} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">{isHindi ? 'हां' : 'Yes'}</SelectItem>
                            <SelectItem value="no">{isHindi ? 'नहीं' : 'No'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {isHindi ? 'दस्तावेज (यदि कोई हो)' : 'Documents (if any)'}
                        </label>
                        <Textarea 
                          name="documents" 
                          value={formData.documents} 
                          onChange={handleInputChange} 
                          rows={2}
                          placeholder={isHindi ? 'उपलब्ध दस्तावेजों की सूची' : 'List available documents'}
                          data-testid="documents-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Declaration */}
                  <div className="bg-gray-100 p-5 rounded-xl">
                    <div className="flex items-start space-x-3">
                      <Checkbox 
                        id="declaration"
                        checked={declarationChecked}
                        onCheckedChange={setDeclarationChecked}
                        className="mt-1"
                        data-testid="declaration-checkbox"
                      />
                      <label htmlFor="declaration" className="text-sm text-gray-700 cursor-pointer">
                        <CheckCircle className="h-5 w-5 text-green-600 inline mr-2" />
                        {isHindi 
                          ? 'मैं घोषणा करता/करती हूं कि ऊपर दी गई सभी जानकारी सत्य और सही है। मैं समझता/समझती हूं कि झूठी जानकारी देने पर कानूनी कार्रवाई हो सकती है।'
                          : 'I declare that all information provided above is true and correct. I understand that providing false information may lead to legal consequences.'}
                      </label>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-lg py-6" 
                    size="lg" 
                    disabled={isSubmitting}
                    data-testid="submit-application-btn"
                  >
                    {isSubmitting 
                      ? (isHindi ? 'सबमिट हो रहा है...' : 'Submitting...') 
                      : (isHindi ? 'आवेदन सबमिट करें' : 'Submit Application')}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Footer Note */}
            <div className="mt-6 text-center text-sm text-gray-600">
              <p>
                {isHindi 
                  ? '* आवेदन की समीक्षा के बाद हम आपसे संपर्क करेंगे'
                  : '* We will contact you after reviewing your application'}
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* WhatsApp Floating Button */}
      <WhatsAppButton />
    </div>
  );
};

export default BonafideCasePage;
